import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useSyncExternalStore } from 'react';
import { fetchAiCredits } from '@/api/ai';
import { useCompanySwitcher } from '@/hooks/useCompanySwitcher';
import { useFeatures } from '@/hooks/useFeatures';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTranslator } from '@/hooks/useTranslator';
import { hasOldFrontend } from '@/lib/utils/env';
import { isImpersonating } from '@/lib/utils/managementAccess';
import {
  isOldDashboardBannerDismissed,
  setOldDashboardBannerDismissed,
  subscribeOldDashboardBannerDismiss,
} from '@/lib/utils/oldDashboardBannerState';
import { resetTours } from '@/lib/utils/onboardingTourState';
import { endImpersonation, logout } from '@/lib/utils/session';
import { useActivityFeedStore } from '@/store/activityFeedStore';
import { useSessionStore } from '@/store/sessionStore';

/**
 * Everything the client app's user menu offers. The composition renders; this
 * decides what is available and what each entry does.
 */
export function useClientUserMenu() {
  const user = useSessionStore(s => s.user);

  const { isTracingAiEnabled } = useFeatures();
  const navigate = useNavigate();
  const { getTranslation } = useTranslator({ page: 'appHeader' });
  const setNotificationsOpen = useActivityFeedStore(s => s.setOpen);

  const { data: creditsResponse } = useQuery({
    queryKey: ['ai-credits'],
    queryFn: fetchAiCredits,
    enabled: isTracingAiEnabled,
    staleTime: 60_000,
  });

  // The dashboard, because that is the page a customer expects to land on under
  // the company they just picked.
  const switcher = useCompanySwitcher({
    onSwitched: () => void navigate({ to: '/dashboard' }),
  });

  // Drives the "show the old-dashboard banner again" entry, which only makes
  // sense once the banner has been dismissed.
  const bannerDismissed = useSyncExternalStore(
    subscribeOldDashboardBannerDismiss,
    isOldDashboardBannerDismissed,
    () => false,
  );

  // 640px, matching the `sm:` breakpoint the mobile-only notifications entry
  // uses — a different threshold would leave a band where this and the CSS
  // disagree about whether that entry exists.
  const isNarrow = useIsMobile(640);

  return {
    getTranslation,
    companyId: user?.company?.id,
    search: switcher.search,
    setSearch: switcher.setSearch,
    companies: switcher.companies,
    hasCompanies: switcher.hasCompanies,
    credits: creditsResponse?.data ?? null,
    showCredits: isTracingAiEnabled,
    isImpersonating: isImpersonating(user),
    bannerDismissed,
    // A v2-only deployment has no banner to bring back, so the entry that
    // offers it should not be there either.
    hasOldFrontend,
    isNarrow,
    handleSwitchCompany: switcher.handleSwitchCompany,
    onViewCompanyProfile: (companyId: string) =>
      void navigate({ to: '/network/$companyId', params: { companyId } }),
    onProfile: () => void navigate({ to: '/profile' }),
    onCompanySettings: () => void navigate({ to: '/company-settings' }),
    onNotifications: () => setNotificationsOpen(true),
    onEndImpersonation: endImpersonation,
    onRestartTour: resetTours,
    onShowOldDashboardBanner: () => setOldDashboardBannerDismissed(false),
    onLogout: () => void logout(),
  };
}
